import { supabase } from '@/lib/supabase';
import type { Database, DesignerContactRow } from '@/types/database';
import { NO_USAGE, type ContactUsage } from '@/lib/contacts-library';

type DesignerContactInsert = Database['public']['Tables']['designer_contacts']['Insert'];
type DesignerContactUpdate = Database['public']['Tables']['designer_contacts']['Update'];

/**
 * Contacts are a DESIGNER-level library since migration 0018. A contact exists
 * once; projects reference it through project_contacts.
 *
 * The legacy per-project `contacts` table still exists in the database as the
 * rollback path for 0018, but nothing in the app reads it any more. Don't add
 * queries against it.
 */

/** Everyone on a project. */
export async function listContacts(projectId: string): Promise<DesignerContactRow[]> {
  const { data, error } = await supabase
    .from('project_contacts')
    .select('designer_contacts!inner(*)')
    .eq('project_id', projectId);
  if (error) throw error;
  return (data ?? [])
    .map((r) => (r as unknown as { designer_contacts: DesignerContactRow }).designer_contacts)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The designer's whole address book — the "Add from my contacts" picker. */
export async function listDesignerContacts(): Promise<DesignerContactRow[]> {
  const { data, error } = await supabase
    .from('designer_contacts')
    .select('*')
    .order('name', { ascending: true });
  if (error) throw error;
  return data ?? [];
}

/**
 * Create a contact in the library and, when a project is given, add them to it.
 *
 * Both halves matter: a contact created from a project page should appear in
 * the library for reuse, and should also show up on the project the designer
 * was looking at.
 */
export async function createContact(
  input: Omit<DesignerContactInsert, 'designer_id'>,
  projectId?: string,
): Promise<DesignerContactRow> {
  const { data: session } = await supabase.auth.getUser();
  const designerId = session.user?.id;
  if (!designerId) throw new Error('Not signed in');

  const { data, error } = await supabase
    .from('designer_contacts')
    .insert({ ...input, designer_id: designerId })
    .select()
    .single();
  if (error) throw error;

  const contact = data as DesignerContactRow;
  if (projectId) await addContactToProject(projectId, contact.id);
  return contact;
}

export async function updateContact(
  id: string,
  patch: DesignerContactUpdate,
): Promise<DesignerContactRow> {
  const { data, error } = await supabase
    .from('designer_contacts')
    .update(patch)
    .eq('id', id)
    .select()
    .single();
  if (error) throw error;
  return data as DesignerContactRow;
}

/** Add an existing library contact to a project. Idempotent. */
export async function addContactToProject(
  projectId: string,
  contactId: string,
): Promise<void> {
  const { error } = await supabase
    .from('project_contacts')
    .upsert({ project_id: projectId, designer_contact_id: contactId },
            { onConflict: 'project_id,designer_contact_id' });
  if (error) throw error;
}

/**
 * Take someone off a project WITHOUT deleting them from the library.
 *
 * This is what the project page's remove action does. Deleting the person
 * outright is a separate, rarer decision — it would cascade to their
 * assignments and portal links on every other project.
 */
export async function removeContactFromProject(
  projectId: string,
  contactId: string,
): Promise<void> {
  const { error } = await supabase
    .from('project_contacts')
    .delete()
    .eq('project_id', projectId)
    .eq('designer_contact_id', contactId);
  if (error) throw error;
}

/**
 * How every contact in the firm is used, for the contacts page.
 *
 * Four counts, four queries, all scoped by RLS to the caller's firm. Counted
 * here rather than in the database because each one is a plain select the app
 * already has rules for — a view or an RPC would need its own.
 *
 * `punch_items` is queried through the contact id alone: the column arrived in
 * 0040 and the table's rules already scope it to the firm's rooms.
 */
export async function contactUsage(): Promise<Map<string, ContactUsage>> {
  const [members, tokens, assignments, punch] = await Promise.all([
    supabase.from('project_contacts').select('designer_contact_id, project_id'),
    supabase.from('contractor_portal_tokens').select('contact_id'),
    supabase.from('contractor_assignments').select('contact_id'),
    supabase.from('punch_items').select('designer_contact_id').not('designer_contact_id', 'is', null),
  ]);
  for (const r of [members, tokens, assignments, punch]) if (r.error) throw r.error;

  const usage = new Map<string, ContactUsage>();
  const bump = (id: string | null, key: keyof ContactUsage): void => {
    if (!id) return;
    const u = usage.get(id) ?? { ...NO_USAGE };
    u[key] += 1;
    usage.set(id, u);
  };
  for (const r of members.data ?? []) bump((r as { designer_contact_id: string }).designer_contact_id, 'projects');
  for (const r of tokens.data ?? []) bump((r as { contact_id: string }).contact_id, 'portalLinks');
  for (const r of assignments.data ?? []) bump((r as { contact_id: string }).contact_id, 'assignments');
  for (const r of punch.data ?? []) bump((r as { designer_contact_id: string }).designer_contact_id, 'punchItems');
  return usage;
}

/** Remove from the library entirely. Cascades to every project. */
export async function deleteContact(id: string): Promise<void> {
  const { error } = await supabase.from('designer_contacts').delete().eq('id', id);
  if (error) throw error;
}
